import { Color3, Mesh, MeshBuilder, PBRMaterial, TransformNode, Vector3, type Scene } from "@babylonjs/core";

export type Holiday = "christmas" | "halloween" | "july4th" | null;

/**
 * Ambient prop dressing — independent of any specific vignette playing,
 * matching the original screensaver-concept's behavior of ambient
 * decoration (e.g. a banner) alongside a dedicated holiday vignette.
 * St Patrick's and New Year's are still Phase 5+ follow-ups, not done here.
 *
 * Also owns the raft prop (not holiday-related — see `setRaftVisible`),
 * since it's the same "toggle a persistent procedural prop on/off" pattern.
 */
export class EnvironmentDecorator {
  private readonly christmasOrnaments: Mesh[];
  private readonly jackOLantern: TransformNode;
  private readonly flag: TransformNode;
  private readonly raft: TransformNode;
  private current: Holiday = null;

  constructor(scene: Scene, palmCrownPosition: Vector3, groundOrigin: Vector3) {
    this.christmasOrnaments = this.buildChristmasOrnaments(scene, palmCrownPosition);
    this.jackOLantern = this.buildJackOLantern(scene, groundOrigin.add(new Vector3(1.1, 0, 0.6)));
    this.flag = this.buildFlag(scene, groundOrigin.add(new Vector3(-1.1, 0, 0.6)));
    this.raft = this.buildRaft(scene, groundOrigin.add(new Vector3(0, 0, 2.8)));
    this.setHoliday(null);
    this.setRaftVisible(false);
  }

  setHoliday(holiday: Holiday): void {
    this.current = holiday;
    for (const ornament of this.christmasOrnaments) ornament.setEnabled(holiday === "christmas");
    this.jackOLantern.setEnabled(holiday === "halloween");
    this.flag.setEnabled(holiday === "july4th");
  }

  getHoliday(): Holiday {
    return this.current;
  }

  setRaftVisible(visible: boolean): void {
    this.raft.setEnabled(visible);
  }

  private buildChristmasOrnaments(scene: Scene, crown: Vector3): Mesh[] {
    const colors = [new Color3(0.7, 0.08, 0.08), new Color3(0.85, 0.65, 0.15), new Color3(0.1, 0.35, 0.55)];
    const ornaments: Mesh[] = [];
    for (let i = 0; i < 8; i++) {
      const sphere = MeshBuilder.CreateSphere(`christmasOrnament${i}`, { diameter: 0.16, segments: 8 }, scene);
      const mat = new PBRMaterial(`christmasOrnamentMaterial${i}`, scene);
      mat.albedoColor = colors[i % colors.length];
      // No scene environment texture to reflect, so metallic stays 0 (see
      // JohnnyCharacter.ts's note — nonzero metallic + no env = pure black)
      // — the "glossy ornament" read comes from low roughness catching a
      // tight direct-light highlight instead.
      mat.metallic = 0;
      mat.roughness = 0.2;
      mat.environmentIntensity = 0;
      sphere.material = mat;

      const angle = (i / 8) * Math.PI * 2;
      const radius = 0.9 + (i % 2) * 0.3;
      const drop = 0.3 + (i % 3) * 0.25;
      sphere.position = crown.add(new Vector3(Math.cos(angle) * radius, -drop, Math.sin(angle) * radius));
      sphere.freezeWorldMatrix(); // never moves — toggling setEnabled() doesn't need this unfrozen (Phase 4 perf pass)
      ornaments.push(sphere);
    }
    return ornaments;
  }

  private buildJackOLantern(scene: Scene, position: Vector3): TransformNode {
    const root = new TransformNode("jackOLanternRoot", scene);
    root.position = position;

    const pumpkinMat = new PBRMaterial("pumpkinMaterial", scene);
    pumpkinMat.albedoColor = new Color3(0.85, 0.42, 0.08);
    pumpkinMat.roughness = 0.6;
    pumpkinMat.metallic = 0;
    pumpkinMat.environmentIntensity = 0;

    const body = MeshBuilder.CreateSphere("pumpkinBody", { diameter: 0.6, segments: 10 }, scene);
    body.scaling = new Vector3(1, 0.8, 1); // squashed, pumpkin-like proportions
    body.position.y = 0.24;
    body.parent = root;
    body.material = pumpkinMat;

    const stemMat = new PBRMaterial("pumpkinStemMaterial", scene);
    stemMat.albedoColor = new Color3(0.32, 0.4, 0.14);
    stemMat.roughness = 0.7;
    stemMat.environmentIntensity = 0;
    const stem = MeshBuilder.CreateCylinder("pumpkinStem", { diameterTop: 0.04, diameterBottom: 0.08, height: 0.16 }, scene);
    stem.position.y = 0.52;
    stem.parent = root;
    stem.material = stemMat;

    // A carved face: small dark cutout-style boxes overlaid on the front
    // hemisphere — cheap and reads fine at the distance this is viewed
    // from, rather than actually boolean-cutting the sphere geometry.
    const faceMat = new PBRMaterial("pumpkinFaceMaterial", scene);
    faceMat.albedoColor = new Color3(0.08, 0.04, 0.02);
    faceMat.roughness = 0.9;
    faceMat.environmentIntensity = 0;

    const eyeGeom = { width: 0.09, height: 0.09, depth: 0.02 };
    const leftEye = MeshBuilder.CreateBox("pumpkinEyeL", eyeGeom, scene);
    leftEye.position = new Vector3(-0.13, 0.3, 0.29);
    leftEye.rotation.y = -0.4;
    leftEye.rotation.z = 0.3;
    leftEye.parent = root;
    leftEye.material = faceMat;

    const rightEye = MeshBuilder.CreateBox("pumpkinEyeR", eyeGeom, scene);
    rightEye.position = new Vector3(0.13, 0.3, 0.29);
    rightEye.rotation.y = 0.4;
    rightEye.rotation.z = -0.3;
    rightEye.parent = root;
    rightEye.material = faceMat;

    const mouth = MeshBuilder.CreateBox("pumpkinMouth", { width: 0.24, height: 0.06, depth: 0.02 }, scene);
    mouth.position = new Vector3(0, 0.14, 0.3);
    mouth.parent = root;
    mouth.material = faceMat;

    root.getChildMeshes().forEach((m) => m.freezeWorldMatrix());
    return root;
  }

  private buildFlag(scene: Scene, position: Vector3): TransformNode {
    const root = new TransformNode("flagRoot", scene);
    root.position = position;

    const poleMat = new PBRMaterial("flagPoleMaterial", scene);
    poleMat.albedoColor = new Color3(0.4, 0.38, 0.34);
    poleMat.roughness = 0.5;
    poleMat.environmentIntensity = 0;
    const pole = MeshBuilder.CreateCylinder("flagPole", { diameter: 0.04, height: 1.4 }, scene);
    pole.position.y = 0.7;
    pole.parent = root;
    pole.material = poleMat;

    // A generic red/white/blue tricolor — festive/patriotic for a July 4th
    // moment without reproducing any specific real flag's exact design.
    const stripeColors = [new Color3(0.75, 0.12, 0.12), new Color3(0.9, 0.9, 0.88), new Color3(0.12, 0.22, 0.6)];
    const stripeHeight = 0.14;
    stripeColors.forEach((color, i) => {
      const mat = new PBRMaterial(`flagStripeMaterial${i}`, scene);
      mat.albedoColor = color;
      mat.roughness = 0.8;
      mat.environmentIntensity = 0;
      mat.backFaceCulling = false;
      mat.twoSidedLighting = true;

      const stripe = MeshBuilder.CreatePlane(`flagStripe${i}`, { width: 0.5, height: stripeHeight }, scene);
      stripe.position = new Vector3(0.29, 1.15 - i * stripeHeight, 0);
      // A flat 90° rotation happened to sit almost exactly edge-on to the
      // july4th vignette's camera, reading as a thin dark sliver instead
      // of a visible striped flag — 45° reads clearly from a wider range
      // of angles instead of gambling on one exact viewing direction.
      stripe.rotation.y = Math.PI / 4;
      stripe.parent = root;
      stripe.material = mat;
    });

    root.getChildMeshes().forEach((m) => m.freezeWorldMatrix());
    return root;
  }

  private buildRaft(scene: Scene, position: Vector3): TransformNode {
    const root = new TransformNode("raftRoot", scene);
    root.position = position;

    const woodMat = new PBRMaterial("raftWoodMaterial", scene);
    woodMat.albedoColor = new Color3(0.45, 0.33, 0.2);
    woodMat.roughness = 0.8;
    woodMat.environmentIntensity = 0;

    // A handful of parallel "logs" for the deck, plus two cross-beams —
    // enough to read as a raft at the distances this project's vignettes
    // frame things from, without needing real log-texture detail.
    for (let i = 0; i < 5; i++) {
      const log = MeshBuilder.CreateCylinder(`raftLog${i}`, { diameter: 0.22, height: 2.2, tessellation: 8 }, scene);
      log.rotation.z = Math.PI / 2;
      log.position = new Vector3(0, 0.11, -1 + i * 0.5);
      log.parent = root;
      log.material = woodMat;
    }
    for (const z of [-0.7, 0.7]) {
      const beam = MeshBuilder.CreateCylinder(`raftBeam${z}`, { diameter: 0.14, height: 2, tessellation: 8 }, scene);
      beam.rotation.x = Math.PI / 2;
      beam.position = new Vector3(0, 0.22, z);
      beam.parent = root;
      beam.material = woodMat;
    }

    root.getChildMeshes().forEach((m) => m.freezeWorldMatrix());
    return root;
  }

  dispose(): void {
    for (const ornament of this.christmasOrnaments) ornament.dispose();
    this.jackOLantern.dispose();
    this.flag.dispose();
    this.raft.dispose();
  }
}
